import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  FiClock, FiCalendar, FiCheckCircle, FiXCircle, FiAlertCircle, FiUser,
  FiChevronDown, FiChevronUp, FiCoffee, FiPause, FiHeart, FiMoreHorizontal,
  FiCheckSquare, FiEdit, FiEyeOff, FiSend, FiEdit2, FiPlusCircle, FiTrendingUp,
  FiActivity, FiSun, FiThermometer, FiHome, FiRefreshCw, FiBriefcase, FiCircle
} from 'react-icons/fi'
import SolicitudesHorasExtras from '../SolicitudesHorasExtras'
import toast from 'react-hot-toast'
import { canAccess, getEffectivePlan } from '../../config/planFeatures'
import { useAuth } from '../../contexts/AuthContext'
import PropuestaCorreccionModal from '../../components/v2/PropuestaCorreccionModal'
import PropuestaMarcajeModal from '../../components/v2/PropuestaMarcajeModal'
import CorreccionesDiaAdmin from '../../components/v2/CorreccionesDiaAdmin'
import { API_URL } from '../../config/api'
import { useEmpresaGestion } from '../../contexts/EmpresaGestionContext'
import { PageHeader, Card, Btn, Empty, Pill, Avatar } from '../../ui'
import { formatearFecha } from '../../utils/fechas'

/*
 * GestionFichajes (V2) — versión rediseñada con DS v2.
 * Misma lógica funcional que el original (tabs, contadores, sub-tabs, modales).
 * Cambian:
 *   - PageHeader v2 con eyebrow numerada
 *   - Tabs principales con DS v2 (rounded-pill bg-bg2)
 *   - Filtros en pills v2
 *   - Card primitive en lugar de card-glass
 *   - Pill para badges de estado y tipo
 *   - Btn primitive para acciones
 *   - Modales con ui-card + Btn
 */

const TIPOS_PAUSA = {
  comida:   { nombre: 'Comida',   icono: FiCoffee },
  descanso: { nombre: 'Descanso', icono: FiPause },
  medico:   { nombre: 'Médico',   icono: FiHeart },
  personal: { nombre: 'Personal', icono: FiUser },
  otro:     { nombre: 'Otro',     icono: FiMoreHorizontal }
}

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

// Badge del estado de una solicitud ya resuelta. Se usa en el histórico para no
// mostrar botones de Aprobar/Rechazar sobre solicitudes aprobadas o rechazadas.
const EstadoBadge = ({ estado }) => (
  <Pill tone={estado === 'aprobada' ? 'emerald' : estado === 'rechazada' ? 'red' : 'neutral'}>
    {estado === 'aprobada' ? 'Aprobada' : estado === 'rechazada' ? 'Rechazada' : estado}
  </Pill>
)

function FilterSelect({ icon: Icon, label, value, onChange, children }) {
  return (
    <div className="inline-flex items-center gap-1.5 h-9 px-3 rounded-pill bg-[var(--bg-2)] border border-[var(--line)] text-[12.5px]">
      {Icon && <Icon size={13} className="text-[var(--ink-3)] flex-shrink-0" />}
      <label className="text-[var(--ink-3)] whitespace-nowrap">{label}</label>
      <select value={value} onChange={onChange} className="bg-transparent outline-none text-[var(--ink)] font-medium cursor-pointer">
        {children}
      </select>
    </div>
  )
}

function TabBtn({ active, onClick, icon: Icon, children, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-pill text-[13px] font-medium transition-colors ${
        active
          ? 'bg-[var(--ink)] text-[var(--bg)]'
          : 'text-[var(--ink-2)] hover:bg-[var(--bg-2)]'
      }`}
    >
      {Icon && <Icon size={14} />}
      {children}
      {count > 0 && (
        <span className={`text-[10px] font-mono tnum px-1.5 py-0.5 rounded-full ${
          active ? 'bg-[var(--bg)]/20 text-[var(--bg)]' : 'bg-[rgba(239,68,68,.10)] text-[#C13030]'
        }`}>
          {count}
        </span>
      )}
    </button>
  )
}

function SubTabBtn({ active, onClick, icon: Icon, children, count, accent = 'brand' }) {
  const accentColors = {
    brand: '#2563EB',
    purple: '#7C3AED',
    emerald: '#0F8C66',
    amber: '#A86A06',
    indigo: '#4338CA',
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors ${
        active ? '' : 'border-transparent text-[var(--ink-3)] hover:text-[var(--ink-2)]'
      }`}
      style={active ? { borderColor: accentColors[accent], color: accentColors[accent] } : undefined}
    >
      {Icon && <Icon size={14} />}
      {children}
      {count > 0 && (
        <span
          className="text-[10px] font-mono tnum px-1.5 py-0.5 rounded-full"
          style={{ background: `${accentColors[accent]}1A`, color: accentColors[accent] }}
        >
          {count}
        </span>
      )}
    </button>
  )
}

const getEstadoFichaje = (fichaje) => {
  if (fichaje.hora_salida) return { tone: 'emerald', label: 'Completo' }
  if (fichaje.estado === 'pausado') return { tone: 'amber', label: 'Pausado' }
  return { tone: 'brand', label: 'En curso' }
}

// Mapeo de estado → meta visual (icono, tono Pill, ring, descripción).
// El backend produce los códigos en `estado` y `etiqueta`. Aquí solo añadimos
// la presentación para la card.
const META_ESTADO_AHORA = {
  trabajando:        { icon: FiActivity,        tone: 'emerald', ring: 'ring-emerald-200',
    desc: 'Tiene fichaje de entrada activo y no está en pausa ahora mismo.' },
  trabajando_con_permiso: { icon: FiActivity,   tone: 'purple',  ring: 'ring-purple-200',
    desc: 'Ha fichado entrada aunque tiene un permiso aprobado para hoy.' },
  comiendo:          { icon: FiCoffee,          tone: 'amber',   ring: 'ring-amber-200',
    desc: 'En pausa de comida.' },
  descansando:       { icon: FiPause,           tone: 'amber',   ring: 'ring-amber-200',
    desc: 'En pausa breve de descanso.' },
  en_medico:         { icon: FiHeart,           tone: 'red',     ring: 'ring-red-200',
    desc: 'Pausa por visita médica.' },
  asunto:            { icon: FiUser,            tone: 'purple',  ring: 'ring-purple-200',
    desc: 'Pausa por asunto personal.' },
  pausa:             { icon: FiPause,           tone: 'amber',   ring: 'ring-amber-200',
    desc: 'En pausa.' },
  vacaciones:        { icon: FiSun,             tone: 'brand',   ring: 'ring-blue-200',
    desc: 'Día de vacaciones aprobado.' },
  baja_laboral:      { icon: FiBriefcase,       tone: 'red',     ring: 'ring-red-200',
    desc: 'De baja laboral.' },
  baja_medica:       { icon: FiThermometer,     tone: 'red',     ring: 'ring-red-200',
    desc: 'De baja médica.' },
  dia_libre:         { icon: FiHome,            tone: 'brand',   ring: 'ring-blue-200',
    desc: 'Día libre disfrutado.' },
  permiso:           { icon: FiCalendar,        tone: 'purple',  ring: 'ring-purple-200',
    desc: 'Permiso aprobado (retribuido, justificado o asuntos propios).' },
  jornada_terminada: { icon: FiCheckCircle,     tone: 'neutral', ring: 'ring-zinc-200',
    desc: 'Ya cerró su jornada de hoy.' },
  sin_fichar:        { icon: FiCircle,          tone: 'neutral', ring: 'ring-zinc-200',
    desc: 'Sin fichaje hoy y sin ausencia aprobada.' },
}

const FILTROS_AHORA = [
  { key: 'todos',     label: 'Todos' },
  { key: 'activos',   label: 'Activos',     match: e => ['trabajando', 'trabajando_con_permiso', 'comiendo', 'descansando', 'en_medico', 'asunto', 'pausa'].includes(e.estado) },
  { key: 'pausados',  label: 'En pausa',    match: e => ['comiendo', 'descansando', 'en_medico', 'asunto', 'pausa'].includes(e.estado) },
  { key: 'ausentes',  label: 'Ausentes',    match: e => ['vacaciones', 'baja_laboral', 'baja_medica', 'dia_libre', 'permiso'].includes(e.estado) },
  { key: 'sin_fichar',label: 'Sin fichar',  match: e => e.estado === 'sin_fichar' },
  { key: 'cerrados',  label: 'Cerrados',    match: e => e.estado === 'jornada_terminada' },
]

function ChipFiltro({ active, onClick, children, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-[11.5px] rounded-full border transition ${
        active
          ? 'bg-[rgba(37,99,235,.06)] border-[var(--brand-1)] text-[var(--brand-1)]'
          : 'bg-[var(--bg-2)] border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--line)]'
      }`}
    >
      {children}
      {typeof count === 'number' && (
        <span className="text-[10px] font-mono tnum opacity-80">{count}</span>
      )}
    </button>
  )
}

function CardEmpleadoAhora({ item }) {
  const meta = META_ESTADO_AHORA[item.estado] || META_ESTADO_AHORA.sin_fichar
  const Icon = meta.icon
  const detalleHora = (() => {
    if (!item.desde) return null
    if (item.estado === 'jornada_terminada') return `${item.desde} → ${item.hasta || '—'}`
    if (['vacaciones','baja_laboral','baja_medica','dia_libre','permiso'].includes(item.estado)) {
      if (item.hasta && item.hasta !== item.desde) return `Hasta ${item.hasta}`
      return null
    }
    return `Desde ${item.desde}`
  })()
  return (
    <div
      className={`flex items-start gap-3 p-3.5 rounded-soft bg-[var(--card)] border border-[var(--line)] ring-1 ${meta.ring} ring-offset-0`}
      title={meta.desc}
    >
      <Avatar name={item.empleado.nombre || '?'} size={36} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="font-medium text-[13px] text-[var(--ink)] truncate">
            {item.empleado.nombre}
          </div>
          <Pill tone={meta.tone}>
            <Icon size={11} />
            {item.etiqueta}
          </Pill>
        </div>
        {item.empleado.grupo_nombre && (
          <div className="text-[11px] text-[var(--ink-3)] truncate">{item.empleado.grupo_nombre}</div>
        )}
        {detalleHora && (
          <div className="mt-1 text-[11px] font-mono tnum text-[var(--ink-2)]">{detalleHora}</div>
        )}
        {item.detalle && (
          <div className="mt-1 text-[11px] text-[var(--ink-3)] italic truncate" title={item.detalle}>
            {item.detalle}
          </div>
        )}
      </div>
    </div>
  )
}

function TabAhora({ empleados, generadoEn, loading, filtro, onFiltro, busqueda, onBusqueda, onRefresh }) {
  const conteos = (() => {
    const out = {}
    FILTROS_AHORA.forEach(f => {
      out[f.key] = f.match ? empleados.filter(f.match).length : empleados.length
    })
    return out
  })()
  const filtrados = empleados.filter(item => {
    const f = FILTROS_AHORA.find(x => x.key === filtro)
    if (!f || !f.match) return true
    return f.match(item)
  }).filter(item => {
    if (!busqueda.trim()) return true
    const q = busqueda.trim().toLowerCase()
    return (item.empleado.nombre || '').toLowerCase().includes(q)
  })

  const horaGen = (() => {
    if (!generadoEn) return null
    // generado_en viene como 'YYYY-MM-DDTHH:MM:SS' (Madrid)
    const parts = generadoEn.split('T')[1] || ''
    return parts.slice(0, 5)
  })()

  return (
    <div className="space-y-4">
      {/* Filtros + buscador + actualizar */}
      <div className="flex flex-wrap items-center gap-2">
        {FILTROS_AHORA.map(f => (
          <ChipFiltro
            key={f.key}
            active={filtro === f.key}
            onClick={() => onFiltro(f.key)}
            count={conteos[f.key]}
          >
            {f.label}
          </ChipFiltro>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <input
            type="text"
            value={busqueda}
            onChange={(e) => onBusqueda(e.target.value)}
            placeholder="Buscar empleado…"
            className="h-8 px-3 text-[12px] rounded-pill bg-[var(--bg-2)] border border-[var(--line)] outline-none focus:border-[var(--ink-3)] w-[200px]"
          />
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-1.5 h-8 px-3 text-[11.5px] rounded-pill bg-[var(--bg-2)] border border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--line)]"
            title="Actualizar ahora"
          >
            <FiRefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>
      </div>

      {horaGen && (
        <div className="text-[11px] text-[var(--ink-3)]">
          Snapshot · {horaGen} · auto-refresco cada 60 s
        </div>
      )}

      {loading && empleados.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[88px] rounded-soft bg-[var(--bg-2)] animate-pulse" />
          ))}
        </div>
      ) : filtrados.length === 0 ? (
        <Card>
          <Empty
            title={empleados.length === 0 ? 'Sin empleados.' : 'Sin coincidencias.'}
            sub={empleados.length === 0
              ? 'No hay empleados activos en la empresa.'
              : 'Ningún empleado encaja con el filtro o la búsqueda actuales.'}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtrados.map(item => (
            <CardEmpleadoAhora key={item.empleado.id} item={item} />
          ))}
        </div>
      )}
    </div>
  )
}

const GestionFichajesV2 = () => {
  const { empresaInfo } = useAuth()
  const plan = getEffectivePlan(empresaInfo)
  const { empresaId: empresaGestionId, esGestion } = useEmpresaGestion()
  const [searchParams] = useSearchParams()
  // Compatibilidad de bookmarks: el tab "Horas extras" antes era principal
  // (?tab=horasExtras). Ahora vive como sub-tab dentro de Correcciones, así
  // que mapeamos el bookmark viejo al nuevo emplazamiento.
  const tabInicial = (() => {
    const t = searchParams.get('tab')
    if (t === 'horasExtras') return 'correcciones'
    if (t && ['fichajes', 'correcciones', 'ahora'].includes(t)) return t
    return 'ahora'
  })()
  const [activeTab, setActiveTab] = useState(tabInicial)
  const [loading, setLoading] = useState(true)

  // Estado "Ahora": snapshot en vivo de empleados y su estado actual
  const [estadoAhora, setEstadoAhora] = useState([])
  const [estadoAhoraGen, setEstadoAhoraGen] = useState(null)
  const [estadoAhoraLoading, setEstadoAhoraLoading] = useState(false)
  const [estadoAhoraFiltro, setEstadoAhoraFiltro] = useState('todos')
  const [estadoAhoraBusqueda, setEstadoAhoraBusqueda] = useState('')

  const [fichajes, setFichajes] = useState([])
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState('')
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1)
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear())
  const [fichajeExpandido, setFichajeExpandido] = useState(null)

  const [correcciones, setCorrecciones] = useState([])
  const [contadorCorrecciones, setContadorCorrecciones] = useState(0)
  const [ausencias, setAusencias] = useState([])
  const [ausenciasPasadas, setAusenciasPasadas] = useState([])
  const [contadorAusencias, setContadorAusencias] = useState(0)
  const [diasSinFichaje, setDiasSinFichaje] = useState([])
  const [contadorControl, setContadorControl] = useState(0)
  const [contadorHorasExtras, setContadorHorasExtras] = useState(0)
  const [estadoFiltroControl, setEstadoFiltroControl] = useState('pendiente')
  const [subTabCorrecciones, setSubTabCorrecciones] = useState(() => {
    // Si la URL trae ?tab=horasExtras o ?subtab=horasExtras, abrimos el
    // sub-tab nuevo de horas extras dentro del bloque de Correcciones.
    const t = searchParams.get('tab')
    const st = searchParams.get('subtab')
    if (t === 'horasExtras' || st === 'horasExtras') return 'horasExtras'
    if (st && ['solicitudes', 'ausencias', 'permisos', 'sinFichaje'].includes(st)) return st
    return 'solicitudes'
  })
  // Filtro estado solicitudes: 'pendiente' (default) | 'historico'
  const [estadoFiltroSolicitudes, setEstadoFiltroSolicitudes] = useState('pendiente')
  const [diasSeleccionados, setDiasSeleccionados] = useState([])
  // Selección múltiple compartida entre Horario, Ausencias y Permisos.
  // Un solo state porque solo un sub-tab está visible a la vez; lo reseteamos
  // en cada cambio de sub-tab y de filtro pendiente/histórico.
  const [seleccionadosCorr, setSeleccionadosCorr] = useState([])
  // IDs cuyas peticiones bulk fallaron en el último intento. Se marcan
  // visualmente en el listado para que el admin sepa cuál revisar y se
  // limpian al cambiar filtros, sub-tab o al volver a procesarlos.
  const [idsFallidos, setIdsFallidos] = useState(new Set())
  const [modalPropuestaMarcaje, setModalPropuestaMarcaje] = useState(null)
  const [empleados, setEmpleados] = useState([])

  const [modalComentario, setModalComentario] = useState({
    // ids es siempre un array; en acciones individuales viene con un único id
    visible: false, tipo: '', accion: '', ids: [], titulo: '', placeholder: '', requerido: false
  })
  const [comentarioTexto, setComentarioTexto] = useState('')

  const [fichajeParaPropuesta, setFichajeParaPropuesta] = useState(null)
  const [modalAdminEdit, setModalAdminEdit] = useState(null)
  const [modalAdminInsert, setModalAdminInsert] = useState(null)
  const [adminEditForm, setAdminEditForm] = useState({ hora_entrada: '', hora_salida: '', motivo: '' })
  const [adminInsertForm, setAdminInsertForm] = useState({ hora_entrada: '', hora_salida: '', motivo: '' })
  const [savingAdmin, setSavingAdmin] = useState(false)

  const buildApiUrl = (endpoint) => {
    if (esGestion && empresaGestionId) {
      const sep = endpoint.includes('?') ? '&' : '?'
      return `${API_URL}${endpoint}${sep}empresa_id=${empresaGestionId}`
    }
    return `${API_URL}${endpoint}`
  }

  // idOrIds puede ser un id individual (number) o un array (acción masiva).
  const abrirModalComentario = (tipo, accion, idOrIds) => {
    const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds]
    const bulk = ids.length > 1
    let titulo = '', placeholder = '', requerido = false
    if (tipo === 'control_ignorar') {
      titulo = bulk ? `Ignorar ${ids.length} días sin fichaje` : 'Ignorar día sin fichaje'
      placeholder = 'Motivo (festivo, día no laborable, etc.)'
      requerido = false
    } else if (accion === 'aprobar') {
      const sustantivo = tipo === 'correccion' ? 'corrección' : 'ausencia'
      titulo = bulk
        ? `Aprobar ${ids.length} ${sustantivo === 'corrección' ? 'correcciones' : 'ausencias'}`
        : `Aprobar ${sustantivo}`
      placeholder = bulk ? 'Comentario común para todas (opcional)' : 'Comentario (opcional)'
      requerido = false
    } else {
      const sustantivo = tipo === 'correccion' ? 'corrección' : 'ausencia'
      titulo = bulk
        ? `Rechazar ${ids.length} ${sustantivo === 'corrección' ? 'correcciones' : 'ausencias'}`
        : `Rechazar ${sustantivo}`
      placeholder = bulk ? 'Motivo del rechazo (se aplica a todas)' : 'Motivo del rechazo'
      requerido = true
    }
    setModalComentario({ visible: true, tipo, accion, ids, titulo, placeholder, requerido })
    setComentarioTexto('')
  }

  const handleEnviarComentario = async () => {
    if (modalComentario.requerido && !comentarioTexto.trim()) {
      toast.error('El comentario es requerido')
      return
    }
    const { tipo, accion, ids } = modalComentario
    setModalComentario({ ...modalComentario, visible: false })

    if (ids.length === 1) {
      // Individual: usa los handlers existentes que ya recargan estado y emiten toast.
      const id = ids[0]
      if (tipo === 'correccion') await handleResponderCorreccion(id, accion, comentarioTexto)
      else if (tipo === 'ausencia') await handleResponderAusencia(id, accion, comentarioTexto)
      else if (tipo === 'control_ignorar') await handleIgnorarDia(id, comentarioTexto)
    } else {
      // Bulk: silenciamos los toasts individuales y mostramos resumen al final.
      // Iteramos en serie para no saturar SQLite con escrituras concurrentes.
      const token = localStorage.getItem('token')
      const endpoint = (id) => {
        if (tipo === 'correccion') return buildApiUrl(`/correcciones/${id}/responder`)
        if (tipo === 'ausencia') return buildApiUrl(`/ausencias/${id}/responder`)
        return buildApiUrl(`/control-fichajes/${id}/ignorar`)
      }
      const body = (id) => tipo === 'control_ignorar'
        ? JSON.stringify({ observaciones: comentarioTexto })
        : JSON.stringify({ accion, comentario: comentarioTexto })

      let ok = 0
      const falladosIds = []
      for (const id of ids) {
        try {
          const r = await fetch(endpoint(id), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: body(id),
          })
          if (r.ok) ok++; else falladosIds.push(id)
        } catch { falladosIds.push(id) }
      }
      const ko = falladosIds.length

      if (ok > 0 && ko === 0) toast.success(`${ok} solicitud${ok > 1 ? 'es' : ''} procesada${ok > 1 ? 's' : ''}`)
      else if (ok > 0 && ko > 0) toast(`${ok} OK · ${ko} con error. Revisa las marcadas en rojo.`, { icon: '⚠️', duration: 6000 })
      else toast.error(`No se pudo procesar ninguna solicitud (${ko} ${ko > 1 ? 'errores' : 'error'})`)

      // Recargas
      if (tipo === 'correccion') cargarCorrecciones()
      else if (tipo === 'ausencia') cargarAusencias()
      else if (tipo === 'control_ignorar') cargarDiasSinFichaje()
      cargarContadores()

      // Mantenemos los fallidos seleccionados para que el admin pueda
      // reintentar fácil; los exitosos se desmarcan.
      setSeleccionadosCorr(falladosIds)
      setDiasSeleccionados(prev => prev.filter(id => falladosIds.includes(id)))
      setIdsFallidos(new Set(falladosIds))
    }

    setComentarioTexto('')
  }

  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['fichajes', 'correcciones', 'ahora'].includes(tabParam)) setActiveTab(tabParam)
  }, [searchParams])

  useEffect(() => {
    cargarEmpleados()
    cargarContadores()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaGestionId])

  const cargarContadores = async () => {
    try {
      const token = localStorage.getItem('token')
      const hoy = new Date().toISOString().split('T')[0]
      let numCorrecciones = 0, numAusenciasPasadas = 0

      const r1 = await fetch(buildApiUrl('/correcciones/todas?estado=pendiente'), { headers: { 'Authorization': `Bearer ${token}` } })
      if (r1.ok) numCorrecciones = (await r1.json()).length

      const r2 = await fetch(buildApiUrl('/ausencias/todas?estado=pendiente'), { headers: { 'Authorization': `Bearer ${token}` } })
      if (r2.ok) {
        const data = await r2.json()
        numAusenciasPasadas = data.filter(a => a.fecha_fin < hoy).length
        setContadorAusencias(data.filter(a => a.fecha_fin >= hoy).length)
      }

      setContadorCorrecciones(numCorrecciones + numAusenciasPasadas)

      const r3 = await fetch(buildApiUrl('/control-fichajes/contador'), { headers: { 'Authorization': `Bearer ${token}` } })
      if (r3.ok) setContadorControl((await r3.json()).count || 0)

      const r4 = await fetch(buildApiUrl('/horas-extras/todas?estado=pendiente'), { headers: { 'Authorization': `Bearer ${token}` } })
      if (r4.ok) {
        const data = await r4.json()
        // El endpoint devuelve un array, o {solicitudes: [...]} según versión.
        const lista = Array.isArray(data) ? data : (data.solicitudes || [])
        setContadorHorasExtras(lista.length)
      }
    } catch (error) {
      console.error('Error cargando contadores:', error)
    }
  }

  useEffect(() => {
    if (activeTab === 'fichajes') cargarFichajes()
    else if (activeTab === 'correcciones') {
      cargarCorrecciones()
      cargarDiasSinFichaje()
      cargarAusencias()
    } else if (activeTab === 'ahora') {
      cargarEstadoAhora()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, empleadoSeleccionado, mesSeleccionado, anioSeleccionado, estadoFiltroControl, estadoFiltroSolicitudes])

  // Auto-refresh del tab Ahora cada 60s mientras esté activo
  useEffect(() => {
    if (activeTab !== 'ahora') return
    const id = setInterval(cargarEstadoAhora, 60000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, empresaGestionId])

  const cargarEstadoAhora = async () => {
    try {
      setEstadoAhoraLoading(true)
      const token = localStorage.getItem('token')
      const r = await fetch(buildApiUrl('/fichajes/estado-actual'), {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (!r.ok) {
        console.error('Estado actual: HTTP', r.status)
        setEstadoAhora([])
        return
      }
      const data = await r.json()
      setEstadoAhora(Array.isArray(data?.empleados) ? data.empleados : [])
      setEstadoAhoraGen(data?.generado_en || null)
    } catch (e) {
      console.error('Error cargando estado actual:', e)
    } finally {
      setEstadoAhoraLoading(false)
    }
  }

  // Reset selección múltiple al cambiar sub-tab o filtro pendiente/histórico:
  // los IDs seleccionados de un sub-tab no aplican al siguiente.
  useEffect(() => {
    setSeleccionadosCorr([])
    setIdsFallidos(new Set())
  }, [subTabCorrecciones, estadoFiltroSolicitudes])

  useEffect(() => {
    let fechaActual = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })
    const checkDayChange = setInterval(() => {
      const nuevaFecha = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })
      if (nuevaFecha !== fechaActual) {
        fechaActual = nuevaFecha
        const ahora = new Date()
        const mesActual = (ahora.getMonth() + 1).toString().padStart(2, '0')
        const anioActual = ahora.getFullYear().toString()
        if (mesSeleccionado === mesActual && anioSeleccionado === anioActual) {
          cargarContadores()
          if (activeTab === 'fichajes') cargarFichajes()
          else if (activeTab === 'correcciones') {
            cargarCorrecciones()
            cargarDiasSinFichaje()
          }
        }
      }
    }, 60000)
    return () => clearInterval(checkDayChange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, mesSeleccionado, anioSeleccionado])

  const cargarEmpleados = async () => {
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(buildApiUrl('/empleados'), { headers: { 'Authorization': `Bearer ${token}` } })
      if (r.ok) setEmpleados((await r.json()).empleados || [])
    } catch (e) { console.error(e) }
  }

  const cargarFichajes = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      let endpoint = `/fichajes/todos?mes=${mesSeleccionado}&anio=${anioSeleccionado}`
      if (empleadoSeleccionado) endpoint += `&empleado_id=${empleadoSeleccionado}`
      const r = await fetch(buildApiUrl(endpoint), { headers: { 'Authorization': `Bearer ${token}` } })
      if (r.ok) setFichajes(await r.json())
    } catch { toast.error('Error al cargar fichajes') }
    finally { setLoading(false) }
  }

  const cargarCorrecciones = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const estadoQuery = estadoFiltroSolicitudes === 'historico' ? 'todas' : 'pendiente'
      let endpoint = `/correcciones/todas?estado=${estadoQuery}`
      if (empleadoSeleccionado) endpoint += `&empleado_id=${empleadoSeleccionado}`
      const r = await fetch(buildApiUrl(endpoint), { headers: { 'Authorization': `Bearer ${token}` } })
      if (r.ok) {
        let data = await r.json()
        if (estadoFiltroSolicitudes === 'historico') data = data.filter(c => c.estado !== 'pendiente')
        setCorrecciones(data)
      }
    } catch { toast.error('Error al cargar correcciones') }
    finally { setLoading(false) }
  }

  const cargarAusencias = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const estadoQuery = estadoFiltroSolicitudes === 'historico' ? 'todas' : 'pendiente'
      let endpoint = `/ausencias/todas?estado=${estadoQuery}`
      if (empleadoSeleccionado) endpoint += `&empleado_id=${empleadoSeleccionado}`
      const r = await fetch(buildApiUrl(endpoint), { headers: { 'Authorization': `Bearer ${token}` } })
      if (r.ok) {
        let data = await r.json()
        if (estadoFiltroSolicitudes === 'historico') data = data.filter(a => a.estado !== 'pendiente')
        const hoy = new Date().toISOString().split('T')[0]
        setAusenciasPasadas(data.filter(a => a.fecha_fin < hoy))
        setAusencias(data.filter(a => a.fecha_fin >= hoy))
      }
    } catch { toast.error('Error al cargar ausencias') }
    finally { setLoading(false) }
  }

  const cargarDiasSinFichaje = async () => {
    setLoading(true)
    setDiasSeleccionados([])
    try {
      const token = localStorage.getItem('token')
      let endpoint = `/control-fichajes/dias-sin-fichaje?estado=${estadoFiltroControl}`
      if (empleadoSeleccionado) endpoint += `&empleado_id=${empleadoSeleccionado}`
      const r = await fetch(buildApiUrl(endpoint), { headers: { 'Authorization': `Bearer ${token}` } })
      if (r.ok) setDiasSinFichaje(await r.json())
    } catch { toast.error('Error al cargar días sin fichaje') }
    finally { setLoading(false) }
  }

  const handleIgnorarDia = async (id, observaciones = '') => {
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(buildApiUrl(`/control-fichajes/${id}/ignorar`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ observaciones })
      })
      if (r.ok) { toast.success('Día marcado como ignorado'); cargarDiasSinFichaje(); cargarContadores() }
      else { toast.error((await r.json()).error || 'Error al ignorar día') }
    } catch { toast.error('Error al ignorar día') }
  }

  const toggleDiaSeleccionado = (diaId) =>
    setDiasSeleccionados(prev => prev.includes(diaId) ? prev.filter(id => id !== diaId) : [...prev, diaId])

  const toggleSeleccionarTodos = () => {
    const pendientes = diasSinFichaje.filter(d => d.estado === 'pendiente')
    if (diasSeleccionados.length === pendientes.length && diasSeleccionados.length > 0) setDiasSeleccionados([])
    else setDiasSeleccionados(pendientes.map(d => d.id))
  }

  // ── Selección múltiple para Horario / Ausencias / Permisos ──
  const toggleSelCorr = (id) =>
    setSeleccionadosCorr(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const limpiarSelCorr = () => setSeleccionadosCorr([])
  // Solo se permite seleccionar items pendientes (los aprobados/rechazados no
  // tienen sentido seleccionar para una acción bulk).
  const seleccionarTodosCorr = (items, idKey = 'id', estadoKey = 'estado') => {
    const elegibles = items.filter(it => (it[estadoKey] || 'pendiente') === 'pendiente').map(it => it[idKey])
    const todosSeleccionados = elegibles.length > 0 && elegibles.every(id => seleccionadosCorr.includes(id))
    setSeleccionadosCorr(todosSeleccionados ? [] : elegibles)
  }

  const abrirPropuestaMarcaje = (dias) => setModalPropuestaMarcaje(Array.isArray(dias) ? dias : [dias])

  const handlePropuestaMarcajeSuccess = () => {
    setModalPropuestaMarcaje(null)
    setDiasSeleccionados([])
    cargarDiasSinFichaje()
    cargarContadores()
  }

  const handleResponderCorreccion = async (id, accion, comentario = '') => {
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(buildApiUrl(`/correcciones/${id}/responder`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ accion, comentario })
      })
      if (r.ok) {
        toast.success(`Corrección ${accion === 'aprobar' ? 'aprobada' : 'rechazada'}`)
        cargarCorrecciones(); cargarContadores()
      } else { toast.error((await r.json()).error || 'Error al procesar solicitud') }
    } catch { toast.error('Error al procesar solicitud') }
  }

  const handleResponderAusencia = async (id, accion, comentario = '') => {
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(buildApiUrl(`/ausencias/${id}/responder`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ accion, comentario })
      })
      if (r.ok) {
        toast.success(`Ausencia ${accion === 'aprobar' ? 'aprobada' : 'rechazada'}`)
        cargarAusencias(); cargarContadores()
      } else { toast.error((await r.json()).error || 'Error al procesar solicitud') }
    } catch { toast.error('Error al procesar solicitud') }
  }

  // B6 — El admin solicita cancelar una ausencia aprobada (la confirma el empleado).
  const handleSolicitarCancelacionAusencia = async (id) => {
    const motivo = window.prompt('Motivo de la cancelación (opcional):', '')
    if (motivo === null) return
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(buildApiUrl(`/ausencias/${id}/solicitar-cancelacion`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ motivo })
      })
      if (r.ok) { toast.success('Cancelación solicitada. Pendiente de que el empleado la confirme.'); cargarAusencias() }
      else { toast.error((await r.json()).error || 'Error al solicitar cancelación') }
    } catch { toast.error('Error al solicitar cancelación') }
  }

  // B6 — El admin responde a una cancelación que solicitó el empleado.
  const handleResponderCancelacionAusencia = async (id, accion) => {
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(buildApiUrl(`/ausencias/${id}/responder-cancelacion`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ accion })
      })
      if (r.ok) {
        toast.success(accion === 'confirmar' ? 'Permiso cancelado' : 'Cancelación rechazada; el permiso sigue vigente')
        cargarAusencias(); cargarContadores()
      } else { toast.error((await r.json()).error || 'Error al procesar la cancelación') }
    } catch { toast.error('Error al procesar la cancelación') }
  }

  // B6 — Área de acciones de una ausencia para el admin (compartida por las
  // sub-pestañas Ausencias y Permisos): aprobar/rechazar si está pendiente, y
  // el flujo de cancelación con doble aprobación si está aprobada.
  const renderAccionesAusencia = (a) => {
    if (a.estado === 'pendiente') {
      return (
        <div className="flex items-center gap-2">
          <Btn variant="primary" size="sm" onClick={() => abrirModalComentario('ausencia', 'aprobar', a.id)}>
            <FiCheckCircle size={13} /> Aprobar
          </Btn>
          <Btn variant="ghost" size="sm" onClick={() => abrirModalComentario('ausencia', 'rechazar', a.id)}>
            <FiXCircle size={13} /> Rechazar
          </Btn>
        </div>
      )
    }
    if (a.estado === 'aprobada') {
      // Cancelación solicitada por el empleado → el admin la confirma/rechaza.
      if (a.cancelacion_estado === 'solicitada' && a.cancelacion_solicitada_por_rol === 'empleado') {
        return (
          <div className="flex flex-col items-end gap-1.5">
            <span className="text-[11px] text-[#A86A06] text-right max-w-[200px]">
              El empleado solicita cancelar
              {a.cancelacion_motivo ? <span className="italic"> «{a.cancelacion_motivo}»</span> : null}
            </span>
            <div className="flex items-center gap-2">
              <Btn variant="primary" size="sm" onClick={() => handleResponderCancelacionAusencia(a.id, 'confirmar')}>
                <FiCheckCircle size={13} /> Confirmar
              </Btn>
              <Btn variant="ghost" size="sm" onClick={() => handleResponderCancelacionAusencia(a.id, 'rechazar')}>
                <FiXCircle size={13} /> Rechazar
              </Btn>
            </div>
          </div>
        )
      }
      // Cancelación solicitada por el admin → pendiente de que confirme el empleado.
      if (a.cancelacion_estado === 'solicitada' && a.cancelacion_solicitada_por_rol === 'admin') {
        return (
          <div className="flex flex-col items-end gap-1.5">
            <EstadoBadge estado={a.estado} />
            <span className="text-[11px] text-[var(--ink-3)] italic">Cancelación pendiente del empleado</span>
          </div>
        )
      }
      // Aprobada sin cancelación en curso → el admin puede solicitarla.
      return (
        <div className="flex flex-col items-end gap-1.5">
          <EstadoBadge estado={a.estado} />
          <Btn variant="ghost" size="sm" onClick={() => handleSolicitarCancelacionAusencia(a.id)}>
            <FiXCircle size={13} /> Solicitar cancelación
          </Btn>
        </div>
      )
    }
    return <EstadoBadge estado={a.estado} />
  }

  const handleAdminEditFichaje = async () => {
    if (!adminEditForm.hora_entrada || !adminEditForm.hora_salida) { toast.error('La hora de entrada y salida son obligatorias'); return }
    if (!adminEditForm.motivo.trim()) { toast.error('El motivo es obligatorio'); return }
    setSavingAdmin(true)
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(buildApiUrl(`/fichajes/${modalAdminEdit.id}/admin-edit`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(adminEditForm)
      })
      if (r.ok) { toast.success('Fichaje corregido correctamente'); setModalAdminEdit(null); cargarFichajes() }
      else { toast.error((await r.json()).error || 'Error al corregir fichaje') }
    } catch { toast.error('Error al corregir fichaje') }
    finally { setSavingAdmin(false) }
  }

  const handleAdminInsertFichaje = async () => {
    if (!adminInsertForm.hora_entrada || !adminInsertForm.hora_salida) { toast.error('La hora de entrada y salida son obligatorias'); return }
    if (!adminInsertForm.motivo.trim()) { toast.error('El motivo es obligatorio'); return }
    setSavingAdmin(true)
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(buildApiUrl('/fichajes/admin-insert'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          empleado_id: modalAdminInsert.empleado_id,
          fecha: modalAdminInsert.fecha,
          hora_entrada: adminInsertForm.hora_entrada,
          hora_salida: adminInsertForm.hora_salida,
          motivo: adminInsertForm.motivo
        })
      })
      if (r.ok) { toast.success('Fichaje insertado correctamente'); setModalAdminInsert(null); cargarDiasSinFichaje(); cargarContadores() }
      else { toast.error((await r.json()).error || 'Error al insertar fichaje') }
    } catch { toast.error('Error al insertar fichaje') }
    finally { setSavingAdmin(false) }
  }

  const formatearDuracion = (minutos) => {
    if (!minutos && minutos !== 0) return '-'
    const horas = Math.floor(minutos / 60)
    const mins = minutos % 60
    return `${horas}h ${mins}m`
  }

  const toggleExpandir = (id) => setFichajeExpandido(fichajeExpandido === id ? null : id)
  const getTipoPausaInfo = (tipo) => TIPOS_PAUSA[tipo] || TIPOS_PAUSA.otro

  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)

  const totalContadorCorrecciones = (canAccess(plan, 'correcciones_flujo') ? contadorCorrecciones : 0)
    + contadorControl
    + (canAccess(plan, 'ausencias') ? contadorAusencias : 0)
    + contadorHorasExtras

  const trabajandoAhora = estadoAhora.filter(e => e.estado === 'trabajando' || e.estado === 'trabajando_con_permiso').length
  const subInfo = activeTab === 'fichajes'
    ? `${MESES[mesSeleccionado - 1]} ${anioSeleccionado} · ${fichajes.length} ${fichajes.length === 1 ? 'fichaje' : 'fichajes'}`
    : activeTab === 'correcciones'
    ? `${totalContadorCorrecciones} ${totalContadorCorrecciones === 1 ? 'pendiente' : 'pendientes'}`
    : activeTab === 'ahora'
    ? `${trabajandoAhora} trabajando · ${estadoAhora.length} en plantilla`
    : 'Solicitudes de horas extra de toda la plantilla'

  return (
    <>
      <PageHeader
        num="§ G1"
        kicker="Administración"
        title="Gestión de fichajes."
        italic="Plantilla y solicitudes."
        sub={subInfo}
      />

      <div className="px-7 py-6 space-y-6">
        {/* Tabs principales */}
        <div className="flex flex-wrap items-center gap-1 p-1 rounded-pill bg-[var(--bg-2)] border border-[var(--line)] w-fit">
          <TabBtn active={activeTab === 'ahora'} onClick={() => setActiveTab('ahora')} icon={FiActivity}>
            Ahora
          </TabBtn>
          <TabBtn active={activeTab === 'fichajes'} onClick={() => setActiveTab('fichajes')} icon={FiClock}>
            Fichajes
          </TabBtn>
          <TabBtn
            active={activeTab === 'correcciones'}
            onClick={() => setActiveTab('correcciones')}
            icon={FiAlertCircle}
            count={totalContadorCorrecciones}
          >
            Correcciones
          </TabBtn>
        </div>

        {/* Filtros (no aplican al tab Ahora, que tiene sus propios chips) */}
        {activeTab !== 'ahora' && (
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              icon={FiUser}
              label="Empleado"
              value={empleadoSeleccionado}
              onChange={(e) => setEmpleadoSeleccionado(e.target.value)}
            >
              <option value="">Todos</option>
              {empleados.map(emp => <option key={emp.id} value={emp.id}>{emp.nombre}</option>)}
            </FilterSelect>
            {activeTab === 'fichajes' && (
              <>
                <FilterSelect
                  icon={FiCalendar}
                  label="Mes"
                  value={mesSeleccionado}
                  onChange={(e) => setMesSeleccionado(parseInt(e.target.value))}
                >
                  {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </FilterSelect>
                <FilterSelect
                  label="Año"
                  value={anioSeleccionado}
                  onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
                >
                  {anios.map(a => <option key={a} value={a}>{a}</option>)}
                </FilterSelect>
              </>
            )}
          </div>
        )}

        {/* TAB: AHORA */}
        {activeTab === 'ahora' && (
          <TabAhora
            empleados={estadoAhora}
            generadoEn={estadoAhoraGen}
            loading={estadoAhoraLoading}
            filtro={estadoAhoraFiltro}
            onFiltro={setEstadoAhoraFiltro}
            busqueda={estadoAhoraBusqueda}
            onBusqueda={setEstadoAhoraBusqueda}
            onRefresh={cargarEstadoAhora}
          />
        )}

        {/* TAB: FICHAJES */}
        {activeTab === 'fichajes' && (
          loading ? (
            <Card eyebrow="Detalle" title="Cargando…">
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-10 rounded-soft bg-[var(--bg-2)] animate-pulse" />
                ))}
              </div>
            </Card>
          ) : fichajes.length === 0 ? (
            <Card>
              <Empty title="Sin fichajes." sub="No hay fichajes para mostrar con los filtros actuales." />
            </Card>
          ) : (
            <Card
              eyebrow="Detalle"
              title={`Fichajes — ${MESES[mesSeleccionado - 1]} ${anioSeleccionado}`}
              action={<span className="text-[11.5px] text-[var(--ink-3)] tnum">{fichajes.length} registros</span>}
              bodyClassName="p-0"
            >
              <div className="overflow-x-auto">
                <div className="grid grid-cols-12 px-5 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)] bg-[var(--bg-2)]/50 border-b border-[var(--line)] min-w-[860px]">
                  <div className="col-span-3">Empleado</div>
                  <div className="col-span-2">Fecha</div>
                  <div className="col-span-1">Entrada</div>
                  <div className="col-span-1">Salida</div>
                  <div className="col-span-1">Pausas</div>
                  <div className="col-span-1 text-right">Tiempo</div>
                  <div className="col-span-1">Estado</div>
                  <div className="col-span-2 text-right">Acción</div>
                </div>
                <div className="divide-y divide-[var(--line)] min-w-[860px]">
                  {fichajes.map(fichaje => {
                    const tienePausas = fichaje.pausas && fichaje.pausas.length > 0
                    const estaExpandido = fichajeExpandido === fichaje.id
                    const est = getEstadoFichaje(fichaje)
                    return (
                      <div key={fichaje.id}>
                        <div className="grid grid-cols-12 items-center px-5 py-3 text-[12.5px] hover:bg-[var(--bg-2)]/40 transition-colors">
                          <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                            <Avatar name={fichaje.empleado_nombre || '?'} size={26} />
                            <span className="font-medium truncate">{fichaje.empleado_nombre || '—'}</span>
                          </div>
                          <div className="col-span-2 font-mono tnum text-[var(--ink-2)] flex items-center gap-2">
                            {formatearFecha(fichaje.fecha)}
                            {fichaje.tiene_correccion === 1 && <Pill tone="amber">Corregido</Pill>}
                          </div>
                          <div className="col-span-1 font-mono tnum">{fichaje.hora_entrada}</div>
                          <div className="col-span-1 font-mono tnum">
                            {fichaje.hora_salida || '—'}
                            {fichaje.cross_day && <span className="text-[10px] text-[#A86A06] font-medium ml-1">(+1D)</span>}
                          </div>
                          <div className="col-span-1">
                            {tienePausas ? (
                              <button
                                type="button"
                                onClick={() => toggleExpandir(fichaje.id)}
                                className="inline-flex items-center gap-1 text-[var(--brand-1)] hover:underline"
                              >
                                <Pill tone="amber">{fichaje.pausas.length}</Pill>
                                {estaExpandido ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
                              </button>
                            ) : (
                              <span className="text-[var(--ink-3)]">—</span>
                            )}
                          </div>
                          <div className="col-span-1 text-right font-mono tnum font-medium">
                            {formatearDuracion(fichaje.tiempo_efectivo || fichaje.tiempo_total)}
                          </div>
                          <div className="col-span-1">
                            <Pill tone={est.tone}>{est.label}</Pill>
                          </div>
                          <div className="col-span-2 text-right">
                            {canAccess(plan, 'correcciones_flujo') ? (
                              <button
                                type="button"
                                onClick={() => setFichajeParaPropuesta(fichaje)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-soft text-[11.5px] font-medium text-[var(--brand-1)] hover:bg-[rgba(37,99,235,.06)] transition-colors"
                                title="Proponer corrección al empleado"
                              >
                                <FiSend size={12} /> Proponer
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  setAdminEditForm({
                                    hora_entrada: fichaje.hora_entrada || '',
                                    hora_salida: fichaje.hora_salida || '',
                                    motivo: ''
                                  })
                                  setModalAdminEdit(fichaje)
                                }}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-soft text-[11.5px] font-medium text-[#A86A06] hover:bg-[rgba(245,158,11,.08)] transition-colors"
                                title="Corregir fichaje directamente"
                              >
                                <FiEdit2 size={12} /> Corregir
                              </button>
                            )}
                          </div>
                        </div>
                        {tienePausas && estaExpandido && (
                          <div className="px-5 pb-3 pt-1 bg-[var(--bg-2)]/40">
                            <div className="text-[10px] text-[var(--ink-3)] font-mono uppercase tracking-wider mb-2">Detalle de pausas</div>
                            <div className="flex flex-wrap gap-2">
                              {fichaje.pausas.map((pausa, idx) => {
                                const tp = getTipoPausaInfo(pausa.tipo)
                                const Icono = tp.icono
                                return (
                                  <div key={pausa.id || idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill bg-[var(--bg)] border border-[var(--line)] text-[11.5px]">
                                    <Icono size={11} className="text-[var(--ink-3)]" />
                                    <span className="font-medium text-[var(--ink-2)]">{tp.nombre}:</span>
                                    <span className="font-mono tnum">{pausa.hora_inicio} – {pausa.hora_fin || 'En curso'}</span>
                                    {pausa.duracion_minutos > 0 && (
                                      <span className="font-mono tnum text-[var(--ink-3)]">({formatearDuracion(pausa.duracion_minutos)})</span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </Card>
          )
        )}

        {/* TAB: CORRECCIONES */}
        {activeTab === 'correcciones' && (
          <div className="space-y-4">
            {/* Sub-tabs */}
            <div className="flex flex-wrap gap-1 border-b border-[var(--line)]">
              {canAccess(plan, 'correcciones_flujo') && (
                <SubTabBtn
                  active={subTabCorrecciones === 'solicitudes'}
                  onClick={() => setSubTabCorrecciones('solicitudes')}
                  icon={FiEdit}
                  count={correcciones.filter(c => c.tipo_solicitud !== 'admin_a_empleado').length}
                  accent="brand"
                >
                  Horario
                </SubTabBtn>
              )}
              {canAccess(plan, 'correcciones_flujo') && (
                <SubTabBtn
                  active={subTabCorrecciones === 'ausencias'}
                  onClick={() => setSubTabCorrecciones('ausencias')}
                  icon={FiCalendar}
                  count={ausenciasPasadas.length}
                  accent="purple"
                >
                  Ausencias
                </SubTabBtn>
              )}
              {canAccess(plan, 'ausencias') && (
                <SubTabBtn
                  active={subTabCorrecciones === 'permisos'}
                  onClick={() => setSubTabCorrecciones('permisos')}
                  icon={FiCalendar}
                  count={contadorAusencias}
                  accent="emerald"
                >
                  Permisos
                </SubTabBtn>
              )}
              <SubTabBtn
                active={subTabCorrecciones === 'sinFichaje'}
                onClick={() => setSubTabCorrecciones('sinFichaje')}
                icon={FiCheckSquare}
                count={contadorControl}
                accent="amber"
              >
                Días sin fichaje
              </SubTabBtn>
              <SubTabBtn
                active={subTabCorrecciones === 'horasExtras'}
                onClick={() => setSubTabCorrecciones('horasExtras')}
                icon={FiTrendingUp}
                count={contadorHorasExtras}
                accent="indigo"
              >
                Horas extras
              </SubTabBtn>
            </div>

            {/* Toggle pendientes / histórico (solicitudes, ausencias, permisos) */}
            {(subTabCorrecciones === 'solicitudes' || subTabCorrecciones === 'ausencias' || subTabCorrecciones === 'permisos') && (
              <div className="flex items-center gap-2 -mt-1">
                <span className="text-[11px] text-[var(--ink-3)]">Mostrar:</span>
                <button
                  onClick={() => setEstadoFiltroSolicitudes('pendiente')}
                  className={`px-3 py-1 text-[11px] rounded-full border transition ${
                    estadoFiltroSolicitudes === 'pendiente'
                      ? 'bg-[rgba(37,99,235,.06)] border-[var(--brand-1)] text-[var(--brand-1)]'
                      : 'bg-[var(--bg-2)] border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--line)]'
                  }`}
                >
                  Pendientes
                </button>
                <button
                  onClick={() => setEstadoFiltroSolicitudes('historico')}
                  className={`px-3 py-1 text-[11px] rounded-full border transition ${
                    estadoFiltroSolicitudes === 'historico'
                      ? 'bg-[rgba(37,99,235,.06)] border-[var(--brand-1)] text-[var(--brand-1)]'
                      : 'bg-[var(--bg-2)] border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--line)]'
                  }`}
                >
                  Histórico (aprobadas / rechazadas)
                </button>
              </div>
            )}

            {/* Selección múltiple + acciones bulk (solo en pendientes de los
                3 sub-tabs, no en histórico ni en horas extras). */}
            {(subTabCorrecciones === 'solicitudes' || subTabCorrecciones === 'ausencias' || subTabCorrecciones === 'permisos')
              && estadoFiltroSolicitudes === 'pendiente' && (() => {
              const items = subTabCorrecciones === 'solicitudes'
                ? correcciones.filter(c => c.tipo_solicitud !== 'admin_a_empleado')
                : subTabCorrecciones === 'ausencias' ? ausenciasPasadas
                : ausencias
              if (items.length === 0) return null
              const elegibles = items.map(it => it.id)
              const todosSeleccionados = elegibles.length > 0 && elegibles.every(id => seleccionadosCorr.includes(id))
              const tipoBulk = subTabCorrecciones === 'solicitudes' ? 'correccion' : 'ausencia'
              return (
                <div className="space-y-2 -mt-1">
                  {items.length > 1 && (
                    <label className="inline-flex items-center gap-1.5 text-[12px] text-[var(--ink-3)] cursor-pointer hover:text-[var(--ink-2)]">
                      <input
                        type="checkbox"
                        checked={todosSeleccionados}
                        onChange={() => seleccionarTodosCorr(items)}
                        className="w-3.5 h-3.5 rounded border-[var(--line)] focus:ring-[var(--brand-1)]"
                      />
                      Seleccionar todos
                    </label>
                  )}
                  {seleccionadosCorr.length > 0 && (
                    <div className="flex items-center justify-between p-3 rounded-soft bg-[rgba(37,99,235,.04)] border border-[rgba(37,99,235,.15)]">
                      <span className="text-[13px] font-medium text-[var(--brand-1)]">
                        {seleccionadosCorr.length} seleccionad{seleccionadosCorr.length > 1 ? 'as' : 'a'}
                      </span>
                      <div className="flex gap-2">
                        <Btn variant="ghost" size="sm" onClick={limpiarSelCorr}>Deseleccionar</Btn>
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={() => abrirModalComentario(tipoBulk, 'rechazar', seleccionadosCorr)}
                        >
                          <FiXCircle size={13} /> Rechazar ({seleccionadosCorr.length})
                        </Btn>
                        <Btn
                          variant="primary"
                          size="sm"
                          onClick={() => abrirModalComentario(tipoBulk, 'aprobar', seleccionadosCorr)}
                        >
                          <FiCheckCircle size={13} /> Aprobar ({seleccionadosCorr.length})
                        </Btn>
                      </div>
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Solicitudes de Corrección */}
            {subTabCorrecciones === 'solicitudes' && (
              <div className="space-y-3">
                {/* Punto C — correcciones a nivel de marcaje (modelo nuevo) */}
                <Card bodyClassName="p-4">
                  <CorreccionesDiaAdmin onCambio={() => { cargarCorrecciones(); cargarContadores() }} />
                </Card>

                {/* Correcciones del modelo legacy (solo si quedan datos antiguos) */}
                {correcciones.length === 0 ? (
                  <Card>
                    <Empty title="Sin solicitudes antiguas." sub="Las correcciones nuevas aparecen en el panel de arriba." />
                  </Card>
                ) : correcciones.map(c => (
                  <Card
                    key={c.id}
                    bodyClassName="p-5"
                    className={idsFallidos.has(c.id) ? 'ring-1 ring-red-300 ring-offset-1' : ''}
                  >
                    {idsFallidos.has(c.id) && (
                      <div className="mb-3">
                        <Pill tone="red" title="Hubo un error al procesar esta solicitud en la última operación. Si persiste tras reintentar, consulta con soporte.">
                          <FiAlertCircle size={11} /> Error al procesar — consultar con soporte
                        </Pill>
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      {estadoFiltroSolicitudes === 'pendiente' && c.tipo_solicitud !== 'admin_a_empleado' && (
                        <div className="pt-1">
                          <input
                            type="checkbox"
                            checked={seleccionadosCorr.includes(c.id)}
                            onChange={() => toggleSelCorr(c.id)}
                            className="w-4 h-4 rounded border-[var(--line)] focus:ring-[var(--brand-1)]"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar name={c.empleado_nombre || '?'} size={26} />
                          <span className="font-medium text-[var(--ink)]">{c.empleado_nombre}</span>
                          <span className="text-[12px] text-[var(--ink-3)] font-mono tnum">— {formatearFecha(c.fecha)}</span>
                        </div>
                        {c.pausa_id ? (
                          <div className="mb-3">
                            <Pill tone="amber" className="mb-2">Corrección de pausa</Pill>
                            <div className="grid grid-cols-2 gap-4 text-[12.5px]">
                              <div>
                                <span className="text-[var(--ink-3)]">Pausa original: </span>
                                <span className="font-mono tnum">{c.hora_inicio_pausa_original || '-'} – {c.hora_fin_pausa_original || '-'}</span>
                              </div>
                              <div>
                                {c.solicitar_borrado ? (
                                  <span className="font-medium text-[#C13030]">Solicita borrado de pausa</span>
                                ) : (
                                  <>
                                    <span className="text-[var(--ink-3)]">Corregido: </span>
                                    <span className="font-mono tnum font-medium text-[#0F8C66]">
                                      {c.hora_inicio_pausa_corregida} – {c.hora_fin_pausa_corregida || '-'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-4 text-[12.5px] mb-3">
                            <div>
                              <span className="text-[var(--ink-3)]">Original: </span>
                              <span className="font-mono tnum">{c.hora_entrada_original || '-'} – {c.hora_salida_original || '-'}</span>
                            </div>
                            <div>
                              <span className="text-[var(--ink-3)]">Corregido: </span>
                              <span className="font-mono tnum font-medium text-[#0F8C66]">
                                {c.hora_entrada_corregida} – {c.hora_salida_corregida || '-'}
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="text-[12.5px] text-[var(--ink-2)]">
                          <strong>Motivo:</strong> {c.motivo}
                        </div>
                        {c.tipo_solicitud === 'admin_a_empleado' && (
                          <div className="text-[11px] text-[var(--ink-3)] mt-1">
                            Propuesta por: {c.solicitante_nombre || 'Admin'}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {c.estado !== 'pendiente' ? (
                          <EstadoBadge estado={c.estado} />
                        ) : c.tipo_solicitud === 'admin_a_empleado' ? (
                          <Pill tone="amber">Pendiente de empleado</Pill>
                        ) : (
                          <>
                            <Btn variant="primary" size="sm" onClick={() => abrirModalComentario('correccion', 'aprobar', c.id)}>
                              <FiCheckCircle size={13} /> Aprobar
                            </Btn>
                            <Btn variant="ghost" size="sm" onClick={() => abrirModalComentario('correccion', 'rechazar', c.id)}>
                              <FiXCircle size={13} /> Rechazar
                            </Btn>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Ausencias declaradas */}
            {subTabCorrecciones === 'ausencias' && (
              <div className="space-y-3">
                {ausenciasPasadas.length === 0 ? (
                  <Card>
                    <Empty title="Sin ausencias declaradas." sub="No hay ausencias declaradas pendientes." />
                  </Card>
                ) : ausenciasPasadas.map(a => (
                  <Card
                    key={`aus-${a.id}`}
                    bodyClassName="p-5"
                    className={idsFallidos.has(a.id) ? 'ring-1 ring-red-300 ring-offset-1' : ''}
                  >
                    {idsFallidos.has(a.id) && (
                      <div className="mb-3">
                        <Pill tone="red" title="Hubo un error al procesar esta solicitud en la última operación. Si persiste tras reintentar, consulta con soporte.">
                          <FiAlertCircle size={11} /> Error al procesar — consultar con soporte
                        </Pill>
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      {estadoFiltroSolicitudes === 'pendiente' && (
                        <div className="pt-1">
                          <input
                            type="checkbox"
                            checked={seleccionadosCorr.includes(a.id)}
                            onChange={() => toggleSelCorr(a.id)}
                            className="w-4 h-4 rounded border-[var(--line)] focus:ring-[var(--brand-1)]"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar name={a.empleado_nombre || '?'} size={26} />
                          <span className="font-medium text-[var(--ink)]">{a.empleado_nombre}</span>
                          <Pill tone="purple">{a.tipo}</Pill>
                        </div>
                        <div className="text-[12.5px] mb-2 flex items-center gap-2">
                          <FiCalendar size={13} className="text-[var(--ink-3)]" />
                          <span className="font-mono tnum text-[var(--ink-2)]">
                            {formatearFecha(a.fecha_inicio)} – {formatearFecha(a.fecha_fin)}
                          </span>
                          {a.dias_laborables && (
                            <span className="text-[var(--ink-3)]">({a.dias_laborables} días)</span>
                          )}
                        </div>
                        {a.motivo && (
                          <div className="text-[12.5px] text-[var(--ink-2)]">
                            <strong>Motivo:</strong> {a.motivo}
                          </div>
                        )}
                      </div>
                      {renderAccionesAusencia(a)}
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Días sin fichaje */}
            {subTabCorrecciones === 'sinFichaje' && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {estadoFiltroControl === 'pendiente' && diasSinFichaje.filter(d => d.estado === 'pendiente').length > 1 && (
                    <label className="inline-flex items-center gap-1.5 text-[12px] text-[var(--ink-3)] cursor-pointer hover:text-[var(--ink-2)]">
                      <input
                        type="checkbox"
                        checked={diasSeleccionados.length === diasSinFichaje.filter(d => d.estado === 'pendiente').length && diasSeleccionados.length > 0}
                        onChange={toggleSeleccionarTodos}
                        className="w-3.5 h-3.5 rounded border-[var(--line)] focus:ring-[var(--brand-1)]"
                      />
                      Seleccionar todos
                    </label>
                  )}
                  <div className="flex items-center gap-2 flex-wrap ml-auto">
                    <span className="text-[11px] text-[var(--ink-3)]">Mostrar:</span>
                    {[
                      { value: 'pendiente',   label: 'Pendientes' },
                      { value: 'justificado', label: 'Justificados' },
                      { value: 'ignorado',    label: 'Ignorados' },
                      { value: 'todos',       label: 'Todos' },
                    ].map((opt) => {
                      const activo = estadoFiltroControl === opt.value
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setEstadoFiltroControl(opt.value)}
                          className={`px-3 py-1 text-[11px] rounded-full border transition ${
                            activo
                              ? 'bg-[rgba(37,99,235,.06)] border-[var(--brand-1)] text-[var(--brand-1)]'
                              : 'bg-[var(--bg-2)] border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--line)]'
                          }`}
                        >
                          {opt.label}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {diasSeleccionados.length > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-soft bg-[rgba(37,99,235,.04)] border border-[rgba(37,99,235,.15)]">
                    <span className="text-[13px] font-medium text-[var(--brand-1)]">
                      {diasSeleccionados.length} día{diasSeleccionados.length > 1 ? 's' : ''} seleccionado{diasSeleccionados.length > 1 ? 's' : ''}
                    </span>
                    <div className="flex gap-2">
                      <Btn variant="ghost" size="sm" onClick={() => setDiasSeleccionados([])}>Deseleccionar</Btn>
                      {canAccess(plan, 'correcciones_flujo') ? (
                        <Btn
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            const dias = diasSinFichaje.filter(d => diasSeleccionados.includes(d.id))
                            abrirPropuestaMarcaje(dias)
                          }}
                        >
                          <FiSend size={13} /> Proponer marcaje ({diasSeleccionados.length})
                        </Btn>
                      ) : (
                        <span className="text-[11.5px] text-[var(--ink-3)]">
                          Selecciona un día e inserta el fichaje individualmente
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {diasSinFichaje.length === 0 ? (
                  <Card>
                    <Empty
                      title="Todo en orden."
                      sub={`No hay días sin fichaje${estadoFiltroControl !== 'todos' ? ` con estado "${estadoFiltroControl}"` : ''}.`}
                    />
                  </Card>
                ) : diasSinFichaje.map(dia => {
                  const estadoTone = dia.estado === 'pendiente' ? 'amber' : dia.estado === 'justificado' ? 'emerald' : 'neutral'
                  return (
                    <Card key={dia.id} bodyClassName="p-5">
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                        {dia.estado === 'pendiente' && (
                          <div className="pt-1">
                            <input
                              type="checkbox"
                              checked={diasSeleccionados.includes(dia.id)}
                              onChange={() => toggleDiaSeleccionado(dia.id)}
                              className="w-4 h-4 rounded border-[var(--line)] focus:ring-[var(--brand-1)]"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Avatar name={dia.empleado_nombre || '?'} size={24} />
                            <span className="font-medium text-[var(--ink)]">{dia.empleado_nombre}</span>
                            <Pill tone={estadoTone}>{dia.estado}</Pill>
                          </div>
                          <div className="text-[12.5px] flex items-center gap-2">
                            <FiCalendar size={13} className="text-[var(--ink-3)]" />
                            <span className="font-mono tnum text-[var(--ink-2)]">{formatearFecha(dia.fecha)}</span>
                          </div>
                          {dia.observaciones && (
                            <div className="text-[12px] text-[var(--ink-3)] mt-1">
                              <strong>Observaciones:</strong> {dia.observaciones}
                            </div>
                          )}
                          {dia.tipo_justificacion && (
                            <div className="text-[12px] text-[var(--ink-3)] mt-1">
                              <strong>Justificado por:</strong> {dia.tipo_justificacion === 'correccion' ? 'Corrección' : 'Ausencia'}
                            </div>
                          )}
                        </div>
                        {dia.estado === 'pendiente' && (
                          <div className="flex items-center gap-2">
                            {canAccess(plan, 'correcciones_flujo') ? (
                              <Btn variant="primary" size="sm" onClick={() => abrirPropuestaMarcaje(dia)}>
                                <FiSend size={13} /> Proponer
                              </Btn>
                            ) : (
                              <Btn
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  setAdminInsertForm({ hora_entrada: '09:00', hora_salida: '17:00', motivo: '' })
                                  setModalAdminInsert(dia)
                                }}
                              >
                                <FiPlusCircle size={13} /> Insertar fichaje
                              </Btn>
                            )}
                            <Btn variant="ghost" size="sm" onClick={() => abrirModalComentario('control_ignorar', 'ignorar', dia.id)}>
                              <FiEyeOff size={13} /> Ignorar
                            </Btn>
                          </div>
                        )}
                      </div>
                    </Card>
                  )
                })}
              </div>
            )}

            {/* Solicitud de Permisos (futuras) */}
            {subTabCorrecciones === 'permisos' && (
              <div className="space-y-3">
                {ausencias.length === 0 ? (
                  <Card>
                    <Empty title="Sin solicitudes." sub="No hay solicitudes de permisos pendientes." />
                  </Card>
                ) : ausencias.map(a => (
                  <Card
                    key={a.id}
                    bodyClassName="p-5"
                    className={idsFallidos.has(a.id) ? 'ring-1 ring-red-300 ring-offset-1' : ''}
                  >
                    {idsFallidos.has(a.id) && (
                      <div className="mb-3">
                        <Pill tone="red" title="Hubo un error al procesar esta solicitud en la última operación. Si persiste tras reintentar, consulta con soporte.">
                          <FiAlertCircle size={11} /> Error al procesar — consultar con soporte
                        </Pill>
                      </div>
                    )}
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                      {estadoFiltroSolicitudes === 'pendiente' && (
                        <div className="pt-1">
                          <input
                            type="checkbox"
                            checked={seleccionadosCorr.includes(a.id)}
                            onChange={() => toggleSelCorr(a.id)}
                            className="w-4 h-4 rounded border-[var(--line)] focus:ring-[var(--brand-1)]"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar name={a.empleado_nombre || '?'} size={26} />
                          <span className="font-medium text-[var(--ink)]">{a.empleado_nombre}</span>
                          <Pill tone="brand">{a.tipo}</Pill>
                        </div>
                        <div className="text-[12.5px] mb-2 flex items-center gap-2">
                          <FiCalendar size={13} className="text-[var(--ink-3)]" />
                          <span className="font-mono tnum text-[var(--ink-2)]">
                            {formatearFecha(a.fecha_inicio)} – {formatearFecha(a.fecha_fin)}
                          </span>
                          <span className="text-[var(--ink-3)]">({a.dias_laborables} días)</span>
                        </div>
                        {a.motivo && (
                          <div className="text-[12.5px] text-[var(--ink-2)]">
                            <strong>Motivo:</strong> {a.motivo}
                          </div>
                        )}
                      </div>
                      {renderAccionesAusencia(a)}
                    </div>
                  </Card>
                ))}
              </div>
            )}
            {subTabCorrecciones === 'horasExtras' && (
              <SolicitudesHorasExtras embedded />
            )}
          </div>
        )}
      </div>

      {/* Modal: Comentario */}
      {modalComentario.visible && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="ui-card w-full max-w-md">
            <div className="px-6 pt-6 pb-3">
              <h3 className="font-display text-[22px] text-[var(--ink)]">{modalComentario.titulo}</h3>
            </div>
            <div className="px-6 pb-4">
              <label className="eyebrow block mb-1.5">
                {modalComentario.placeholder}
                {modalComentario.requerido && <span className="text-[#C13030] ml-1">*</span>}
              </label>
              <textarea
                value={comentarioTexto}
                onChange={(e) => setComentarioTexto(e.target.value)}
                placeholder={modalComentario.placeholder}
                className="w-full h-24 px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] resize-none focus:outline-none focus:border-[var(--ink-3)]"
                autoFocus
              />
            </div>
            <div className="px-6 pb-6 flex justify-end gap-3">
              <Btn
                variant="ghost"
                onClick={() => { setModalComentario({ ...modalComentario, visible: false }); setComentarioTexto('') }}
              >
                Cancelar
              </Btn>
              <Btn variant="primary" onClick={handleEnviarComentario}>
                {modalComentario.tipo === 'control_ignorar' ? 'Ignorar' : modalComentario.accion === 'aprobar' ? 'Aprobar' : 'Rechazar'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Propuesta corrección (componente externo) */}
      {fichajeParaPropuesta && (
        <PropuestaCorreccionModal
          fichaje={fichajeParaPropuesta}
          onClose={() => setFichajeParaPropuesta(null)}
          onSuccess={() => { setFichajeParaPropuesta(null); toast.success('Propuesta enviada al empleado') }}
          buildApiUrl={buildApiUrl}
        />
      )}

      {/* Modal: Propuesta marcaje (componente externo) */}
      {modalPropuestaMarcaje && (
        <PropuestaMarcajeModal
          diasSeleccionados={modalPropuestaMarcaje}
          onClose={() => setModalPropuestaMarcaje(null)}
          onSuccess={handlePropuestaMarcajeSuccess}
          buildApiUrl={buildApiUrl}
        />
      )}

      {/* Modal: Corregir fichaje (Plan Pro) */}
      {modalAdminEdit && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="ui-card w-full max-w-md">
            <div className="px-6 pt-6 pb-3">
              <div className="flex items-center gap-2 mb-2">
                <FiEdit2 size={16} className="text-[#A86A06]" />
                <span className="eyebrow">Corregir fichaje</span>
              </div>
              <h3 className="font-display text-[22px] text-[var(--ink)]">Editar marcaje.</h3>
              <p className="text-[12.5px] text-[var(--ink-3)] mt-1">
                <span className="font-medium text-[var(--ink-2)]">{modalAdminEdit.empleado_nombre}</span>
                {' — '}{formatearFecha(modalAdminEdit.fecha)}
              </p>
            </div>
            <div className="px-6 pb-4">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="eyebrow block mb-1.5">Hora entrada *</label>
                  <input
                    type="time"
                    value={adminEditForm.hora_entrada}
                    onChange={e => setAdminEditForm(f => ({ ...f, hora_entrada: e.target.value }))}
                    className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-1.5">Hora salida *</label>
                  <input
                    type="time"
                    value={adminEditForm.hora_salida}
                    onChange={e => setAdminEditForm(f => ({ ...f, hora_salida: e.target.value }))}
                    className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                  />
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Motivo de la corrección *</label>
                <input
                  type="text"
                  value={adminEditForm.motivo}
                  onChange={e => setAdminEditForm(f => ({ ...f, motivo: e.target.value }))}
                  placeholder="Ej: Error al registrar la salida"
                  className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] focus:outline-none focus:border-[var(--ink-3)]"
                  autoFocus
                />
              </div>
            </div>
            <div className="px-6 pb-6 flex justify-end gap-3">
              <Btn variant="ghost" onClick={() => setModalAdminEdit(null)} disabled={savingAdmin}>
                Cancelar
              </Btn>
              <Btn variant="primary" onClick={handleAdminEditFichaje} disabled={savingAdmin}>
                {savingAdmin ? 'Guardando…' : 'Guardar corrección'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Insertar fichaje (Plan Pro) */}
      {modalAdminInsert && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="ui-card w-full max-w-md">
            <div className="px-6 pt-6 pb-3">
              <div className="flex items-center gap-2 mb-2">
                <FiPlusCircle size={16} className="text-[#A86A06]" />
                <span className="eyebrow">Insertar fichaje</span>
              </div>
              <h3 className="font-display text-[22px] text-[var(--ink)]">Nuevo marcaje.</h3>
              <p className="text-[12.5px] text-[var(--ink-3)] mt-1">
                <span className="font-medium text-[var(--ink-2)]">{modalAdminInsert.empleado_nombre}</span>
                {' — '}{formatearFecha(modalAdminInsert.fecha)}
              </p>
            </div>
            <div className="px-6 pb-4">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="eyebrow block mb-1.5">Hora entrada *</label>
                  <input
                    type="time"
                    value={adminInsertForm.hora_entrada}
                    onChange={e => setAdminInsertForm(f => ({ ...f, hora_entrada: e.target.value }))}
                    className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-1.5">Hora salida *</label>
                  <input
                    type="time"
                    value={adminInsertForm.hora_salida}
                    onChange={e => setAdminInsertForm(f => ({ ...f, hora_salida: e.target.value }))}
                    className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                  />
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Motivo *</label>
                <input
                  type="text"
                  value={adminInsertForm.motivo}
                  onChange={e => setAdminInsertForm(f => ({ ...f, motivo: e.target.value }))}
                  placeholder="Ej: Empleado olvidó fichar"
                  className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] focus:outline-none focus:border-[var(--ink-3)]"
                  autoFocus
                />
              </div>
            </div>
            <div className="px-6 pb-6 flex justify-end gap-3">
              <Btn variant="ghost" onClick={() => setModalAdminInsert(null)} disabled={savingAdmin}>
                Cancelar
              </Btn>
              <Btn variant="primary" onClick={handleAdminInsertFichaje} disabled={savingAdmin}>
                {savingAdmin ? 'Guardando…' : 'Insertar fichaje'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default GestionFichajesV2
